import React, { useEffect, useRef } from "react";
import { useSiteContent } from "../SiteContentContext";
import { useLegalDrawer } from "../LegalDrawerContext";
import { LegalKind, legalDocStem, readLegalHash } from "@Components/legal/legalHash";

/**
 * Deep-link opener for the in-page legal drawer.
 *
 * The privacy / terms / cookie documents on the marketing site are
 * inline modals on the single `/fidava` route — there are no
 * dedicated `/privacy` or `/terms` pages. Some places still need a
 * real URL for those (Google OAuth consent screen, cookie-consent
 * config, payment provider legal links, emails). Two URL shapes open
 * the matching drawer WITHOUT navigating anywhere:
 *
 *   /fidava#policy | #terms | #cookies   (mirrored by the drawer itself:
 *                                         set on open, removed on close)
 *   /fidava?legal=privacy|terms|cookie   (legacy, kept for links already
 *                                         in the wild; handled once per load)
 *
 * Renders nothing.
 */
const QUERY_KINDS: Record<string, LegalKind> = {
  privacy: "privacy",
  terms: "terms",
  cookie: "cookie"
};

const readLegalQuery = (): LegalKind | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  const value = new URLSearchParams(window.location.search).get("legal");
  return value ? QUERY_KINDS[value.trim().toLowerCase()] : undefined;
};

const LegalDeepLink: React.FC = () => {
  const { content, locale } = useSiteContent();
  const { open, close, isOpen } = useLegalDrawer();
  // The query form opens at most once per load: closing the drawer must
  // not re-trigger it (the param stays in the URL so the link remains
  // shareable). The hash form has no such guard — the drawer clears it
  // on close, so a later hashchange is always a fresh request.
  const queryHandledRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  useEffect(() => {
    const openKind = (kind: LegalKind) => {
      // Reuse the exact href + localized label the footer already wires
      // up, so the drawer renders the same document/title. Fall back to
      // the conventional path for the active locale if the footer omits
      // the link.
      const stem = legalDocStem(kind);
      const links = content?.footer?.legal?.links ?? [];
      const match = links.find(
        (link) => typeof link?.href === "string" && link.href.includes(stem)
      );
      const url = match?.href ?? `/legal/${stem}.${locale}.md`;
      const title = match?.label ?? `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
      open(url, title);
    };

    const syncFromHash = () => {
      const kind = readLegalHash();
      if (kind) {
        openKind(kind);
      } else if (isOpenRef.current) {
        close();
      }
    };

    const queryKind = queryHandledRef.current ? undefined : readLegalQuery();
    if (queryKind) {
      queryHandledRef.current = true;
      openKind(queryKind);
    } else if (readLegalHash()) {
      syncFromHash();
    }

    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [content, locale, open, close]);

  return null;
};

export default LegalDeepLink;
